<template>
    <div class="pa-2">
        <v-btn @click="$refs.iuploader.click()" color="zoezi-edit-color" class="mb-2">
            <input ref="iuploader" class="d-none" type="file" :accept="type == 'image' ? 'image/*' : (type == 'video' ? 'video/*' : 'image/*,video/*')" @change="uploadFile($event.target.files[0])">
            <v-icon left>mdi-plus</v-icon>{{ uploadTitle }}
        </v-btn>

        <v-text-field :label="$translate('Search...')" v-model="searchText" />

        <v-item-group active-class="zoezi-edit-color" @change="fileChanged" v-model="selectedFile">
            <v-row v-for="(row, rowindex) in fileRows" :key="rowindex">
                <v-col :cols="12 / columns" v-for="file in row" :key="file.key">
                    <v-hover v-slot:default="{ hover }">
                        <v-item :value="file.key" v-slot:default="{ active, toggle}">
                            <v-card
                                width=200
                                @click="cardClicked($event, file, active, toggle)"
                            >
                                <v-card-text justify="center" align="center" >
                                    <v-auto-img :src="file" height="200px" width="200px">
                                        <v-btn v-if="hover && canShow" icon @click.stop="remove(file)" class="float-right" color="black"><v-icon class="">mdi-close</v-icon></v-btn>
                                    </v-auto-img>
                                </v-card-text>
                            </v-card>
                        </v-item>
                    </v-hover>
                </v-col>
            </v-row>
        </v-item-group>

        <v-btn v-if="limit < files.length"
               text
               color="primary"
               @click="limit += 100"
        >
            {{$translate('Load more')}}
        </v-btn>

        <v-dialog
            v-model="showProgress"
            max-width="500"
        >
            <v-card>
                <v-card-title class="headline zoezi-edit-color">{{ uploadTitle }}</v-card-title>

                <v-card-text class="pa-4">
                    <v-progress-linear
                        active
                        indeterminate
                        size="32"
                        :value="progress"
                        width="2"
                        color="zoezi-edit-color"
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn
                        text
                        color="error"
                        @click="showProgress=false"
                    >
                        {{$translate('Cancel')}}
                    </v-btn>
                </v-card-actions>

            </v-card>

        </v-dialog>

        <v-dialog
            v-model="fileDialog.show"
            max-width="500"
            v-if="fileDialog.show"
        >
            <v-card>
                <v-img :src="$api.getImageUrl(fileDialog.file)" style="width: 500px; height: 500px" contain ref="image" @load="imageLoaded"/>

                <v-card-text class="mt-2">
                    {{$translate('Size')}}: <span v-if="fileDialog.width && fileDialog.height">{{fileDialog.width}} x {{fileDialog.height}} px,</span> {{humanFileSize}}<br>
                    {{$translate('Filename')}}: {{ fileDialog.file.name }}<br>
                    {{$translate('Created')}}: {{ fileDialog.file.created }}<br>
                    {{$translate('Content type')}}: {{ fileDialog.file.content_type }}<br>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn @click="fileDialog.show = false">{{$translate('Close')}}</v-btn>
                </v-card-actions>
            </v-card>

        </v-dialog>
    </div>
</template>

<script>
import {chunk} from 'lodash';
export default {
    name: 'MediaLibrary',
    components: {},
    props: {
        columns: {
            type: Number,
            default: 2
        },
        value: {
            type: String
        },
        type: {
            type: String
        },
        canShow: {
            type: Boolean
        }
    },
    data: () => ({
        files: [],
        selectedFile: null,
        fileToUpload: null,
        showProgress: false,
        progress: 0,
        limit: 100,
        searchText: '',

        fileDialog: {
            show: false,
            file: null,
            width: null,
            height: null
        }
    }),
    watch: {
        value(value) {
            console.log('Value changed for media lib')
            this.selectedFile = value;
        }
    },
    computed: {
        fileRows() {
            return chunk(this.sortedFiles, this.columns);
        },
        filteredFiles() {
            if (!this.searchText) {
                return this.files;
            }
            let s = this.searchText.toLowerCase();
            return this.files.filter(file => {
                return file.name.toLowerCase().indexOf(s) != -1;
            });
        },
        sortedFiles() {
            return this.filteredFiles.concat().sort((a, b) => -a.created.localeCompare(b.created)).slice(0, this.limit);
        },
        humanFileSize() {
            if (!this.fileDialog.show || !this.fileDialog.file) {
                return '';
            }

            let size = this.fileDialog.file.size;
            if (size < 1024) {
                return `${size} bytes`;
            } else if (size < 1024*1024) {
                return `${(size/1024).toFixed(0)} kB`.replace('.', ',');
            } else {
                return `${(size/(1024*1024)).toFixed(1)} MB`.replace('.', ',');
            }
        },
        uploadTitle() {
            const t = this.type == 'image' ? 'image' : (this.type == 'video' ? 'video' : 'file')
            return this.$translate('Upload') + ' ' + this.$translate(t);
        }
    },
    methods: {
        getFileUrl(file, size) {
            return this.$api.getImageUrl(file, size, {notrack: true});
        },
        fileChanged() {
            this.$emit('change', this.selectedFile)
        },
        uploadFile(file) {
            if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                this.$store.commit('showErrorDialog', {
                    title: this.$translate('Failed to upload file'),
                    text: this.$translate('Invalid file type. Only images and videos are allowed.')
                });
                return;
            }

            this.showProgress = true;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('data', JSON.stringify({origin_type: 'homepage'}));
            this.$api.post('/api/utils/file/upload', formData).then(response => response.json().then(data => {
                if (response.ok) {
                    this.files.push(data);
                    this.selectedFile = data.key;
                    this.$emit('change', data.key);
                } else {
                    this.$store.commit('showErrorDialog', {
                        title: this.$translate('Failed to upload file'),
                        text: this.$translate(data.message)
                    });
                }
                // Clear files in iuploader
                this.$refs.iuploader.value = null;
                this.showProgress = false;
            }))
        },
        remove(file) {
            this.$store.commit('showConfirmDialog', {
                title: this.$translate('Delete file'),
                text: this.$translate('Are you sure you want to delete {0}?').format(file.name),
                actionText: this.$translate('Delete'),
                action: () => {
                    this.$api.post('/api/utils/file/remove', {key: file.key}).then(() => {
                        this.loadFiles();
                    })
                }
            });
        },
        loadFiles() {
            let type = this.type;
            let typeMatches = function(x) {
                if (!type) {
                    return true;
                }
                if (type == 'image') {
                    return x.is_image;
                }

                if (type == 'video') {
                    return !x.is_image;
                }

                return true;
            }

            this.$api.post('/api/utils/file/get_list', {origin_type: 'homepage'}).then(response => response.json().then(data => {
                this.files = data.filter(typeMatches).map(x => {
                    let f = {name: x.name, key: x.key, created: x.created, content_type: x.content_type, size: x.size};
                    if (x.is_image && !x.acl && x.storage == 4 && x.variants) f.variants = x.variants.split(',').map(Number);
                    return f;
                });
            }));
        },
        imageLoaded() {
            let image = this.$refs.image.image;
            this.fileDialog.width = image.naturalWidth
            this.fileDialog.height = image.naturalHeight
        },
        cardClicked($event, file, active, toggle) {
            if (this.canShow) {
                this.fileDialog.show = true;
                this.fileDialog.file = file;
            } else {
                if (active) {
                    this.fileChanged();
                } else {
                    toggle($event);
                }
            }
        }
    },
    mounted() {
        this.loadFiles();
    }
}
</script>
